/**
 * Header account control — initial circle + name, opening a popover with Log out.
 *
 * Replaces the bare "Sign Out" button. Identity comes from the auth context (pollis-auth
 * /auth/me), with a UUID guard so a raw user id can never be rendered as a person's name.
 */

import React, { useState } from "react";
import { Box, Menu, MenuItem, Divider, Typography } from "@mui/material";
import { useAuth } from "./lib/AuthContext";
import { C } from "./data";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Cognito's `username` is often the raw `sub`. Never render that as a person's name. */
const clean = (v) => {
  const t = (v || "").trim();
  return !t || UUID_RE.test(t) ? "" : t;
};

/** "Sam Staley" -> "SS" · "sam.staley@x.org" -> "SS" · "sam@x.org" -> "S" */
function initialsFrom(name, email) {
  const src = (name || "").trim();
  if (src) {
    const parts = src.split(/\s+/).filter(Boolean);
    return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
  }
  const local = (email || "").split("@")[0] || "";
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

export default function UserMenu() {
  const [anchor, setAnchor] = useState(null);
  const { user, logout } = useAuth();

  const name = clean(
    user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(" "),
  );
  const email = clean(user?.email);

  const label = name || email.split("@")[0] || "Account";
  const initials = initialsFrom(name, email);
  const open = Boolean(anchor);

  return (
    <>
      <Box
        onClick={(e) => setAnchor(e.currentTarget)}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setAnchor(e.currentTarget);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          borderRadius: 999,
          pl: 0.25,
          pr: 1,
          py: 0.25,
          border: "1px solid transparent",
          transition: "border-color .15s, background .15s",
          "&:hover": {
            borderColor: "#1a3d5c",
            background: "rgba(255,255,255,0.04)",
          },
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: "#074d63", // matches the sign-in button

            color: "#fff",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.02em",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {initials}
        </Box>
        <Typography
          sx={{
            fontSize: 11.5,
            fontWeight: 600,
            color: "#a0b4c8",
            whiteSpace: "nowrap",
            display: { xs: "none", sm: "block" },
          }}
        >
          {label}
        </Typography>
        <Box
          component="span"
          sx={{
            color: "#5a7898",
            fontSize: 9,
            lineHeight: 1,
            display: { xs: "none", sm: "block" },
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .15s",
          }}
        >
          ▼
        </Box>
      </Box>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 1,
              minWidth: 220,
              border: `1px solid ${C.border}`,
              borderRadius: 1,
              boxShadow: "0 6px 24px rgba(10,34,64,0.12)",
            },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.25, pb: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.charcoal }}>
            {label}
          </Typography>
          {email && (
            <Typography sx={{ fontSize: 10.5, color: C.muted, mt: 0.25 }}>
              {email}
            </Typography>
          )}
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            logout();
          }}
          sx={{ fontSize: 12, color: C.charcoal, py: 1 }}
        >
          Log out
        </MenuItem>
      </Menu>
    </>
  );
}
